import React, {FC, ReactNode} from 'react';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisuallyHidden} from '../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {styled} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {getColorHighlight, getEmojiHighlight} from '../../../../helpers/models/highlightHelpers';
import {isTeammateNamespace} from '../../../../helpers/models/namespaceHelpers';
import {SearchTagModel} from '../../../../models/api/searchTagModel';
import {TagModel} from '../../../../models/api/tagModel';
import {Emoji} from '../../emoji/emoji';
import {PickParentTagDropdownContainer} from './pickParentTagDropdownContainer';

/*
 * Props.
 */

interface PickParentTagDropdownBoxProps {
  namespace: string;
  onTagSelect: HandlerOf<SearchTagModel | undefined>;
  selectedTag?: TagModel;
  isDisabled?: boolean;
  excludeTagId?: number;
  label?: ReactNode;
  shouldHideLabel?: boolean;
  dropdownWidth?: number;
}

/*
 * Style.
 */

const StyledFadedDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
  flex: 1;
  ${ellipsis()};
`;

const StyledTagDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledIconDiv = styled.div`
  flex-shrink: 0;
  margin-right: 8px;
`;

const StyledLabelDiv = styled.div`
  margin-right: 3px;
  ${ellipsis()};
`;

/*
 * Component.
 */

export const PickParentTagDropdownBox: FC<PickParentTagDropdownBoxProps> = (props) => (
  <DropdownBoxCoordinator
    label={renderLabel(props)}
    isDisabled={props.isDisabled}
    value={renderValue(props)}
    renderDropdown={(popoverProps) => renderDropdown(popoverProps, props)}
  />
);

function renderLabel({label, shouldHideLabel}: PickParentTagDropdownBoxProps) {
  if (shouldHideLabel) {
    return null;
  }

  return label || <Trans id="choosetagdropdownbox-label">Nest tag under</Trans>;
}

function renderValue({selectedTag}: PickParentTagDropdownBoxProps) {
  if (!selectedTag) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledTagDiv>
            <StyledIconDiv>
              <Icon name="tagSmall" color={theme.greys.shade60} />
            </StyledIconDiv>
            <StyledFadedDiv>
              <Trans id="choosetagdropdownbox-placeholder">Select tag</Trans>
            </StyledFadedDiv>
          </StyledTagDiv>
        )}
      </UserThemeConsumer>
    );
  }

  const isTeammateTag = isTeammateNamespace(selectedTag.namespace);
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledTagDiv>
          <StyledIconDiv>{renderTagIcon(selectedTag)}</StyledIconDiv>
          <StyledLabelDiv>{selectedTag.name}</StyledLabelDiv>
          {isTeammateTag && (
            <StyledIconDiv>
              <VisuallyHidden>
                <Trans id="tags-private">Private</Trans>
              </VisuallyHidden>
              <Icon name="lockFilledTiny" color={theme.greys.shade70} />
            </StyledIconDiv>
          )}
        </StyledTagDiv>
      )}
    </UserThemeConsumer>
  );
}

function renderTagIcon(tag: TagModel) {
  // Emoji highlight.
  const emoji = getEmojiHighlight(tag);
  if (emoji) {
    return <Emoji emoji={emoji.emoji} skinTone={emoji.skinTone} size={16} />;
  }

  // Color highlight.
  const color = getColorHighlight(tag);
  return (
    <UserThemeConsumer>
      {(theme) => (
        <Icon name="tagSmall" color={(color && theme.palette[color].shade50) || theme.greys.shade70} />
      )}
    </UserThemeConsumer>
  );
}

function renderDropdown(popoverProps: PopoverTransferProps, props: PickParentTagDropdownBoxProps) {
  return (
    <PickParentTagDropdownContainer
      {...popoverProps}
      {...props}
      width={props.dropdownWidth || popoverProps.anchorWidth}
    />
  );
}
